// @flow
import React from 'react';
import { connect } from 'react-redux';
import Button from '@localytics/loc-components/lib/Button';
import cs from 'classnames';
import {
  VERSION_STATE_CLOSED,
  VERSION_STATE_DRAFT,
  VERSION_STATE_TERMINATED,
  VERSION_STATE_OPEN,
  type OrchestrationVersion
} from '../constants/ApiTypes';
import Dialog, { DialogControls } from './dialogs/Dialog';
import * as ApiActions from '../actions/api';
import { downloadPerformanceCSV } from '../util/ApiUtils';
import './stylesheets/VersionStatusHeader.scss';

type ModalProps = {
  toggleModal: Function,
  updateVersionState: Function,
  orgId: number,
  orchId: number,
  versionId: number,
  version: OrchestrationVersion
};

type HeaderProps = {
  updateVersionState: Function,
  currentOrchestrationId: number,
  currentOrgId: number,
  currentVersionId: number,
  orchestrationName: string,
  version: OrchestrationVersion,
  activeVersionDraft: string,
  activeVersionId: string | null
};

export const ActivateExistingDraftModal = (
  props: ModalProps & {
    showActivationModal: boolean,
    activeVersionId: string | null,
    activeVersionDraft: string,
    orchestrationName: string
  }
) => {
  const {
    showActivationModal,
    toggleModal,
    updateVersionState,
    orgId,
    orchId,
    versionId,
    activeVersionId,
    activeVersionDraft,
    orchestrationName,
    version
  } = props;
  return (
    <Dialog show={showActivationModal}>
      <div className="VersionModal__Text VersionModal__Text__Primary">
        You are about to <i>Deactivate</i> Draft {activeVersionDraft} of{' '}
        {orchestrationName} and replace it with an Active version of Draft{' '}
        {version.version_number}. Are you sure you want to do this?
      </div>
      <div className="VersionModal__Text VersionModal__Text__Secondary">
        Only one version of a workflow can be{' '}
        <b>
          <i>Active</i>
        </b>{' '}
        at a time. Users who previously qualified will continue to flow through
        the{' '}
        <b>
          <i>Deactivated</i>
        </b>{' '}
        version until they exit naturally, but any additional users must
        requalify for the draft you are about to{' '}
        <b>
          <i>Activate</i>
        </b>.
      </div>
      <DialogControls>
        <Button className="DialogButton" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          className="DialogPrimaryButton"
          onClick={() => {
            toggleModal();
            updateVersionState(
              version,
              orgId,
              orchId,
              +activeVersionId,
              VERSION_STATE_CLOSED
            ).then(() =>
              updateVersionState(
                version,
                orgId,
                orchId,
                versionId,
                VERSION_STATE_OPEN
              )
            );
          }}
          type="primary"
        >
          {"Yes, I'm sure"}
        </Button>
      </DialogControls>
    </Dialog>
  );
};

export const ActivateNewDraftModal = (
  props: ModalProps & {
    showActivationModal: boolean
  }
) => {
  const {
    showActivationModal,
    updateVersionState,
    toggleModal,
    orgId,
    orchId,
    versionId,
    version
  } = props;
  return (
    <Dialog show={showActivationModal}>
      <div className="VersionModal__Text VersionModal__Text__Primary">
        Are you ready to Activate this workflow? Users will be able to enter
        immediately.
      </div>
      <DialogControls>
        <Button className="DialogButton" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          className="DialogPrimaryButton"
          onClick={() => {
            toggleModal();
            updateVersionState(
              version,
              orgId,
              orchId,
              versionId,
              VERSION_STATE_OPEN
            );
          }}
          type="primary"
        >
          Yes, I&#39;m sure
        </Button>
      </DialogControls>
    </Dialog>
  );
};

export const DeactivateActiveModal = (
  props: ModalProps & {
    version: OrchestrationVersion,
    showDeactivationModal: boolean
  }
) => {
  const {
    showDeactivationModal,
    version,
    updateVersionState,
    orgId,
    orchId,
    versionId,
    toggleModal
  } = props;
  return (
    <Dialog show={showDeactivationModal}>
      <div className="VersionModal__Text VersionModal__Text__Primary">
        Are you sure you want to Deactivate Version {version.version_number}?
      </div>
      <div className="VersionModal__Text VersionModal__Text__Secondary">
        Users who have already entered will continue to flow through the{' '}
        <b>
          <i>Deactivated</i>
        </b>{' '}
        version until they exit naturally, but no additional users will be able
        to enter. Alternatively, you can{' '}
        <b>
          <i>Disable</i>
        </b>{' '}
        Version {version.version_number} entirely to remove all users and stop
        all actions immediately.
      </div>
      <DialogControls>
        <Button
          className="DialogButton"
          onClick={() => {
            toggleModal();
            updateVersionState(
              version,
              orgId,
              orchId,
              versionId,
              VERSION_STATE_TERMINATED
            );
          }}
        >
          Disable
        </Button>
        <Button className="DialogButton" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          className="DialogPrimaryButton"
          onClick={() => {
            toggleModal();
            updateVersionState(
              version,
              orgId,
              orchId,
              versionId,
              VERSION_STATE_CLOSED
            );
          }}
          type="primary"
        >
          Yes, Deactivate
        </Button>
      </DialogControls>
    </Dialog>
  );
};

export const TerminateClosedModal = (
  props: ModalProps & {
    showRemoveUsersModal: boolean
  }
) => {
  const {
    showRemoveUsersModal,
    toggleModal,
    orgId,
    orchId,
    versionId,
    updateVersionState,
    version
  } = props;
  return (
    <Dialog show={showRemoveUsersModal}>
      <div className="VersionModal__Text VersionModal__Text__Primary">
        Are you sure you want to Disable Version {version.version_number}?
      </div>
      <div className="VersionModal__Text VersionModal__Text__Secondary">
        If you choose to{' '}
        <b>
          <i>Disable</i>
        </b>{' '}
        Version {version.version_number}, all users who may still be flowing
        through will immediately exit and all actions will be stopped. This
        cannot be undone.
      </div>
      <DialogControls>
        <Button className="DialogButton" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          className="DialogPrimaryButton"
          onClick={() =>
            updateVersionState(
              version,
              orgId,
              orchId,
              versionId,
              VERSION_STATE_TERMINATED
            )
          }
          type="primary"
        >
          Yes, Disable
        </Button>
      </DialogControls>
    </Dialog>
  );
};

const StatusCircle = (props: { versionState: string }) => (
  <svg className="StatusCircle">
    <circle
      cx="5"
      cy="5"
      className={cs('StatusCircle__Ring', {
        StatusCircle__Ring__Draft: props.versionState === 'draft',
        StatusCircle__Ring__Active: props.versionState === 'open',
        StatusCircle__Ring__Closed: props.versionState === 'closed',
        StatusCircle__Ring__Terminated: props.versionState === 'terminated'
      })}
    />
    <circle
      cx="5"
      cy="5"
      className={cs('StatusCircle__InnerRing', {
        StatusCircle__InnerRing__Draft: props.versionState === 'draft',
        StatusCircle__InnerRing__Active: props.versionState === 'open',
        StatusCircle__InnerRing__Closed: props.versionState === 'closed',
        StatusCircle__InnerRing__Terminated: props.versionState === 'terminated'
      })}
    />
  </svg>
);

const DraftHeaderComponents = (
  props: ModalProps & {
    showActivationModal: boolean,
    orchestrationName: string,
    activeVersionDraft: string,
    activeVersionId: string | null
  }
) => {
  const {
    toggleModal,
    activeVersionId,
    version: { state: versionState } = {}
  } = props;
  const deactivateOtherVersion = activeVersionId != null;
  return (
    <div className="VersionStatusHeader__OptionalStatusItems">
      <button className="VersionStatusHeader__Button" onClick={toggleModal}>
        <StatusCircle versionState={versionState} />
        <span className="VersionStatusHeader__Button__Content__Draft" />
      </button>
      {deactivateOtherVersion ? (
        <ActivateExistingDraftModal {...props} />
      ) : (
        <ActivateNewDraftModal {...props} />
      )}
    </div>
  );
};

const ActiveHeaderComponents = (
  props: ModalProps & {
    version: OrchestrationVersion,
    orchestrationName: string,
    showDeactivationModal: boolean
  }
) => {
  const { toggleModal, version: { state: versionState } } = props;
  return (
    <div className="VersionStatusHeader__OptionalStatusItems">
      <button className="VersionStatusHeader__Button" onClick={toggleModal}>
        <StatusCircle versionState={versionState} />
        <span className="VersionStatusHeader__Button__Content__Active" />
      </button>
      <DeactivateActiveModal {...props} />
    </div>
  );
};

const DeactivatingHeaderComponents = (
  props: ModalProps & {
    orchestrationName: string,
    showRemoveUsersModal: boolean
  }
) => {
  const {
    toggleModal,
    orgId,
    orchId,
    orchestrationName,
    version: { state: versionState }
  } = props;
  return (
    <div className="VersionStatusHeader__OptionalStatusItems">
      <button className="VersionStatusHeader__Button" onClick={toggleModal}>
        <StatusCircle versionState={versionState} />
        <span className="VersionStatusHeader__Button__Content__Closed" />
      </button>
      <button
        className="VersionStatusHeader__Button VersionStatusHeader__Button--subdued"
        onClick={() => downloadPerformanceCSV(orgId, orchId, orchestrationName)}
      >
        <svg>
          <use x="0" y="0" fill="#54565C" xlinkHref="#export" />
        </svg>
        Export .CSV
      </button>
      <TerminateClosedModal {...props} />
    </div>
  );
};

const InactiveHeaderComponents = (props: {
  orgId: number,
  orchId: number,
  orchestrationName: string,
  version: OrchestrationVersion
}) => {
  const { version: { state: versionState } } = props;
  return (
    <div className="VersionStatusHeader__OptionalStatusItems">
      <button className="VersionStatusHeader__Button no-select">
        <StatusCircle versionState={versionState} />
        <span className="VersionStatusHeader__Button__Content__Terminated" />
      </button>
      <button
        className="VersionStatusHeader__Button VersionStatusHeader__Button--subdued"
        onClick={() =>
          downloadPerformanceCSV(
            props.orgId,
            props.orchId,
            props.orchestrationName
          )
        }
      >
        <svg>
          <use x="0" y="0" fill="#54565C" xlinkHref="#export" />
        </svg>
        Export .CSV
      </button>
    </div>
  );
};

const OptionalHeaderComponents = (
  props: HeaderProps & {
    showActivationModal: boolean,
    showDeactivationModal: boolean,
    showRemoveUsersModal: boolean,
    activeVersionDraft: string,
    toggleModal: Function
  }
) => {
  const {
    orchestrationName,
    currentOrchestrationId,
    currentOrgId,
    currentVersionId,
    version,
    activeVersionId,
    activeVersionDraft,
    updateVersionState,
    showActivationModal,
    showDeactivationModal,
    showRemoveUsersModal,
    toggleModal
  } = props;
  const { state: versionState = VERSION_STATE_DRAFT } = version || {};
  switch (versionState) {
    case VERSION_STATE_DRAFT:
      return (
        <DraftHeaderComponents
          orgId={currentOrgId}
          orchId={currentOrchestrationId}
          versionId={currentVersionId}
          updateVersionState={updateVersionState}
          activeVersionId={activeVersionId}
          activeVersionDraft={activeVersionDraft}
          orchestrationName={orchestrationName}
          showActivationModal={showActivationModal}
          version={version}
          toggleModal={() => toggleModal('showActivationModal')}
        />
      );
    case VERSION_STATE_OPEN:
      return (
        <ActiveHeaderComponents
          orgId={currentOrgId}
          orchId={currentOrchestrationId}
          versionId={currentVersionId}
          updateVersionState={updateVersionState}
          orchestrationName={orchestrationName}
          version={version}
          showDeactivationModal={showDeactivationModal}
          toggleModal={() => toggleModal('showDeactivationModal')}
        />
      );
    case VERSION_STATE_CLOSED:
      return (
        <DeactivatingHeaderComponents
          orgId={currentOrgId}
          orchId={currentOrchestrationId}
          versionId={currentVersionId}
          updateVersionState={updateVersionState}
          orchestrationName={orchestrationName}
          showRemoveUsersModal={showRemoveUsersModal}
          version={version}
          toggleModal={() => toggleModal('showRemoveUsersModal')}
        />
      );
    case VERSION_STATE_TERMINATED:
      return (
        <InactiveHeaderComponents
          orgId={currentOrgId}
          orchId={currentOrchestrationId}
          orchestrationName={orchestrationName}
          version={version}
        />
      );
    default:
      return null;
  }
};

class VersionStatusHeader extends React.Component<
  HeaderProps,
  {
    showActivationModal: boolean,
    showDeactivationModal: boolean,
    showRemoveUsersModal: boolean
  }
> {
  constructor(props: HeaderProps) {
    super(props);
    this.state = {
      showActivationModal: false,
      showDeactivationModal: false,
      showRemoveUsersModal: false
    };
    this.toggleModal = this.toggleModal.bind(this);
  }

  toggleModal: Function = modal => {
    this.setState({
      [modal]: !this.state[modal]
    });
  };

  render() {
    const {
      showActivationModal,
      showDeactivationModal,
      showRemoveUsersModal
    } = this.state;
    return (
      <OptionalHeaderComponents
        showActivationModal={showActivationModal}
        showDeactivationModal={showDeactivationModal}
        showRemoveUsersModal={showRemoveUsersModal}
        toggleModal={this.toggleModal}
        {...this.props}
      />
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { api: { versionsByOrchestrationId, orchestrationsById } } = state;

  const {
    versionId: currentVersionId,
    orchestrationId: currentOrchestrationId,
    orgId: currentOrgId
  } = ownProps;

  const { name: orchestrationName = '' } =
    orchestrationsById[currentOrchestrationId] || {};
  const currentVersions = versionsByOrchestrationId[currentOrchestrationId];
  const activeVersionId =
    currentVersions &&
    Object.keys(currentVersions).find(
      v => currentVersions[v].state === VERSION_STATE_OPEN
    );

  const version = currentVersions && currentVersions[currentVersionId];

  const activeVersionDraft =
    currentVersions &&
    currentVersions[activeVersionId] &&
    currentVersions[activeVersionId].version_number;

  return {
    orchestrationName,
    currentOrgId,
    currentOrchestrationId,
    currentVersionId,
    version,
    activeVersionId,
    activeVersionDraft
  };
};

const mapDispatchToProps = {
  updateVersionState: ApiActions.updateOrchestrationVersionState
};

export default connect(mapStateToProps, mapDispatchToProps)(
  VersionStatusHeader
);



// WEBPACK FOOTER //
// ./src/components/VersionStatusHeader.jsx